import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useI18n } from "@/i18n";

const STORAGE_KEY = "entracte_cookie_consent";
const GA_MEASUREMENT_ID = "G-7R2MFQ81WN";

function loadGoogleAnalytics() {
  if (window.__ga_loaded) return;
  window.__ga_loaded = true;
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
  }
  // Grant Meta Pixel advanced matching (release LDU)
  try {
    if (typeof window.fbq === "function") {
      window.fbq("dataProcessingOptions", []);
    }
  } catch (_) { /* noop */ }
}

function denyAnalytics() {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  try {
    if (typeof window.fbq === "function") {
      // Re-enable Limited Data Use (RGPD-friendly minimal tracking)
      window.fbq("dataProcessingOptions", ["LDU"], 0, 0);
    }
  } catch (_) { /* noop */ }
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const { t, localePath } = useI18n();

  useEffect(() => {
    // Hidden inside the back-office live preview
    if (window.location.search.includes("preview=1")) {
      denyAnalytics();
      return;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // No choice yet — show banner and default-deny analytics
      denyAnalytics();
      setShow(true);
      return;
    }
    if (saved === "accepted") loadGoogleAnalytics();
    else denyAnalytics();
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    loadGoogleAnalytics();
    setShow(false);
  };

  const refuse = () => {
    localStorage.setItem(STORAGE_KEY, "refused");
    denyAnalytics();
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Consentement aux cookies"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-sm z-[70] bg-white border border-[#D9D3C5] shadow-2xl p-5 animate-fadein"
    >
      <div className="flex items-start gap-3">
        <Cookie size={22} className="text-[#B8956A] shrink-0 mt-1" />
        <div className="flex-1">
          <p className="font-display text-xl text-[#203240]">{t("cookies.title")}</p>
          <p className="mt-2 text-sm text-[#3D4A5A] leading-relaxed">
            {t("cookies.body")}
          </p>
          <p className="mt-2 text-xs text-[#697585]">
            <Link to={localePath("/cookies")} className="link-gold">{t("cookies.more")}</Link> · <Link to={localePath("/politique-de-confidentialite")} className="link-gold">{t("cookies.policy")}</Link>
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={accept}
              data-testid="cookie-accept"
              className="btn-primary py-3 px-5 text-[10px]"
            >
              {t("cookies.accept")}
            </button>
            <button
              onClick={refuse}
              data-testid="cookie-refuse"
              className="btn-ghost py-3 px-5 text-[10px]"
            >
              {t("cookies.refuse")}
            </button>
          </div>
        </div>
        <button onClick={refuse} aria-label="Close" className="text-[#697585] hover:text-[#203240]">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
